// 主线程一侧的任务执行器：优先用 Worker 池并行，Worker 不可用（例如直接 file:// 打开）时退回主线程串行
import { processImage } from "./process";
import type { LogoPayload, WorkerRequest, WorkerResponse } from "./protocol";
import type { ProcessOptions, Settings } from "./types";

export interface Job {
  id: number;
  name: string;
  buf: ArrayBuffer;
}

export interface JobDone {
  id: number;
  blob: Blob;
  outName: string;
}

export interface Runner {
  /** 并行度 */
  readonly parallel: number;
  /** 是否退化为主线程模式 */
  readonly fallback: boolean;
  run(job: Job): Promise<JobDone>;
  close(): void;
}

/** 单个任务的最长等待时间：超过即视为该 Worker 假死，停用它并降级，避免整批卡死 */
const JOB_TIMEOUT_MS = 120_000;

class InlineRunner implements Runner {
  readonly parallel = 1;
  readonly fallback = true;
  private logo: ImageBitmap | null = null;
  private ready: Promise<void>;

  constructor(
    private settings: Settings,
    private options: ProcessOptions,
    logo: LogoPayload | null,
  ) {
    this.ready = this.initLogo(logo);
  }

  private async initLogo(logo: LogoPayload | null): Promise<void> {
    if (logo) this.logo = await createImageBitmap(new Blob([logo.buf], { type: logo.type }));
  }

  async run(job: Job): Promise<JobDone> {
    await this.ready;
    const r = await processImage(job.name, job.buf, this.settings, this.options, this.logo);
    return { id: job.id, blob: r.blob, outName: r.outName };
  }

  close(): void {
    this.logo?.close();
    this.logo = null;
  }
}

class WorkerPool implements Runner {
  private workers: Worker[] = [];
  private ready: boolean[] = [];
  private inflight: number[] = [];
  private waiting: Job[] = [];
  private pending = new Map<number, { resolve: (v: JobDone) => void; reject: (e: Error) => void }>();
  private timers = new Map<number, ReturnType<typeof setTimeout>>();
  private aliveCount: number;
  private dead: boolean[] = [];
  private currentJob: (number | null)[] = [];
  /** 全部 Worker 都不可用后置为 true：其后新提交的任务也直接走主线程 */
  private fallbackMode = false;

  constructor(
    size: number,
    private settings: Settings,
    private options: ProcessOptions,
    private logo: LogoPayload | null,
    private onWarning: (msg: string) => void,
  ) {
    this.aliveCount = size;
    for (let i = 0; i < size; i++) {
      const worker = new Worker(new URL("./worker.js", self.location.href), { type: "module" });
      worker.onmessage = (e: MessageEvent<WorkerResponse>) => this.handleMessage(i, e.data);
      worker.onerror = (e) => this.handleWorkerError(i, e.message);
      this.workers.push(worker);
      this.ready.push(false);
      this.inflight.push(0);
      this.dead.push(false);
      this.currentJob.push(null);
      worker.postMessage({ type: "init", settings, options, logo } satisfies WorkerRequest);
    }
  }

  get parallel(): number {
    return Math.max(1, this.aliveCount);
  }

  get fallback(): boolean {
    return this.fallbackMode;
  }

  run(job: Job): Promise<JobDone> {
    // 已降级：不再排队，直接在主线程处理
    if (this.fallbackMode) return this.inlineOne(job);
    return new Promise<JobDone>((resolve, reject) => {
      this.pending.set(job.id, { resolve, reject });
      this.waiting.push(job);
      this.dispatch();
    });
  }

  close(): void {
    for (const w of this.workers) w.terminate();
    this.workers = [];
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
    this.waiting.length = 0;
    // 明确拒绝未完成的任务，否则调用方的 await 会永远悬着
    for (const entry of this.pending.values()) entry.reject(new Error("任务已取消"));
    this.pending.clear();
  }

  private clearTimer(jobId: number): void {
    const timer = this.timers.get(jobId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(jobId);
    }
  }

  private handleMessage(index: number, msg: WorkerResponse): void {
    if (msg.type === "ready") {
      this.ready[index] = true;
      this.dispatch();
      return;
    }

    this.clearTimer(msg.id);
    const entry = this.pending.get(msg.id);
    this.pending.delete(msg.id);
    this.inflight[index] = 0;
    this.currentJob[index] = null;

    if (msg.type === "done") entry?.resolve({ id: msg.id, blob: msg.blob, outName: msg.outName });
    else entry?.reject(new Error(msg.message));

    this.dispatch();
  }

  private handleWorkerError(index: number, message: string): void {
    if (this.dead[index]) return;
    this.dead[index] = true;
    this.aliveCount = Math.max(1, this.aliveCount - 1);
    this.ready[index] = false;
    this.inflight[index] = 0;
    this.onWarning(`Worker #${index + 1} 异常（${message || "脚本加载失败"}），该线程已停用`);

    // 正在该线程上跑的任务必须明确报错，否则 Promise 永远不会结束
    this.abortCurrentJob(index, `Worker 异常中断: ${message || "脚本加载失败"}`);

    if (!this.ready.some(Boolean)) this.enterFallback();
  }

  private handleTimeout(index: number, jobId: number): void {
    this.timers.delete(jobId);
    if (this.currentJob[index] !== jobId) return;
    this.handleWorkerError(index, `任务超时（>${JOB_TIMEOUT_MS / 1000}s）`);
  }

  private abortCurrentJob(index: number, message: string): void {
    const jobId = this.currentJob[index];
    this.currentJob[index] = null;
    if (jobId === null) return;
    this.clearTimer(jobId);
    const entry = this.pending.get(jobId);
    this.pending.delete(jobId);
    entry?.reject(new Error(message));
  }

  /**
   * 全部 Worker 都不可用时切到主线程：把排队中的任务转交主线程，
   * 并置 fallbackMode，使之后新提交的任务（不再依赖 waiting）也走主线程。
   */
  private enterFallback(): void {
    if (this.fallbackMode) return;
    this.fallbackMode = true;
    this.onWarning("所有 Worker 均不可用，剩余任务改为主线程串行处理");
    const jobs = this.waiting.splice(0);
    for (const job of jobs) {
      const entry = this.pending.get(job.id);
      this.pending.delete(job.id);
      if (!entry) continue;
      this.inlineOne(job).then(entry.resolve, (e) =>
        entry.reject(e instanceof Error ? e : new Error(String(e))),
      );
    }
  }

  private async inlineOne(job: Job): Promise<JobDone> {
    const logo = this.logo
      ? await createImageBitmap(new Blob([this.logo.buf], { type: this.logo.type }))
      : null;
    try {
      const r = await processImage(job.name, job.buf, this.settings, this.options, logo);
      return { id: job.id, blob: r.blob, outName: r.outName };
    } finally {
      logo?.close();
    }
  }

  private dispatch(): void {
    if (this.fallbackMode) return;
    while (this.waiting.length > 0) {
      const index = this.pickIdle();
      if (index < 0) return;
      const job = this.waiting.shift();
      if (!job) return;
      this.inflight[index] = 1;
      this.currentJob[index] = job.id;
      this.timers.set(
        job.id,
        setTimeout(() => this.handleTimeout(index, job.id), JOB_TIMEOUT_MS),
      );
      this.workers[index].postMessage(
        { type: "job", id: job.id, name: job.name, buf: job.buf } satisfies WorkerRequest,
        [job.buf],
      );
    }
  }

  /** 只投递给空闲 Worker，保证一张图在一个 Worker 内独占处理 */
  private pickIdle(): number {
    for (let i = 0; i < this.workers.length; i++) {
      if (this.ready[i] && this.inflight[i] === 0) return i;
    }
    return -1;
  }
}

export function createRunner(
  concurrency: number,
  settings: Settings,
  options: ProcessOptions,
  logo: LogoPayload | null,
  onWarning: (msg: string) => void,
): Runner {
  if (typeof Worker === "undefined") {
    onWarning("当前环境不支持 Web Worker，改为主线程串行处理");
    return new InlineRunner(settings, options, logo);
  }
  try {
    return new WorkerPool(concurrency, settings, options, logo, onWarning);
  } catch (err) {
    onWarning(`无法创建 Worker（${err instanceof Error ? err.message : String(err)}），改为主线程处理`);
    return new InlineRunner(settings, options, logo);
  }
}
